import * as React from 'react';
import { TableCommonProps } from 'react-table';

/**
 * @file
 * Shared types between components and blocks
 */

// Utilities
export function prop<T, K extends keyof T>(object: T, key: K) {
  return object[key];
}
export type ValueOf<T> = T[keyof T];

export interface IDataProps extends IBasstrakkAngler {
  Catches: IBasstrakkCatch[];
  AoyStanding: IAoyAnglerRank;
}
// Bass Table Tabs
export interface IBassTabsProps {
  cutlines: ICutline[];
  sponsors?: ISponsors;
  tid: TournamentId;
  timezone: string;
  tournamentSeries: string;
  type: AoyTournamentType;
}

export interface IAoySectionsType {
  id: number;
  name: [];
}

export interface IRowSeparator {
  numRows: number;
  index: number;
}

// Bassmaster
export interface IDashboardMeta {
  componentName: string;
  nickname: string;
  exampleImgUrl: string;
  pageList: string[];
  renderId: string;
}
// Refresh rates for data. First is desktop/default. Second is mobile.
export type RefreshRate = [number, number];

// Tournament concepts
export type TournamentId = number;
export enum TournamentType {
  PRO = 1,
  CO = 0,
}
export interface IWithTournamentType {
  type: TournamentType;
}

// Tournament 'space' passed into component from Drupal. Corresponds to Drupal
// node.
export enum TournamentClass {
  CLASSIC = 21,
  ELITE = 46,
  OPENS = 51,
  NATION = 56,
  COLLEGE = 36,
  HIGH_SCHOOL = 26,
  TEAM = 31,
  EQS = 41,
}

// Angler concepts
export type AnglerId = number;
export type CatchId = string;
export enum AnglerClass {
  PRO = 1,
  CO = 2,
}

// Tournament + Angler shape from Redis
export interface IAnglerDayRedis {
  DAY: string;
  DAYFISHCOUNT: string;
  DAYWEIGHT: {
    '#': string;
    '@': {
      DAYWEIGHT_OZ: string;
    };
  };
}
export interface IAnglerRankRedis {
  '#': string;
  '@': {
    Row_Num: string;
    TIE: string;
  };
}
export interface IAnglerTotalWeightRedis {
  '#': string;
  '@': {
    TOTALWEIGHT_OZ: string;
  };
}
export interface IAnglerTodayWeightRedis {
  '#': string;
  '@': {
    TODAYWEIGHT_OZ: string;
  };
}
export interface IAnglerRedis {
  ID: string;
  NAME_FONTNAME: string;
  CLASS: string;
  RANK: IAnglerRankRedis;
  PREVDAYS?: {
    DAY: IAnglerDayRedis | IAnglerDayRedis[];
  };
  TODAYBIGBASS: {
    '#': string;
    '@': {
      TODAYBIGBASS_OZ: string;
    };
  };
  TOTALPARTNERWEIGHT: {
    '#': string;
    '@': {
      TOTALPARTNERWEIGHT_OZ: string;
      USECOMBINEDWEIGHT: string;
    };
  };
  TODAYFISHCOUNT: string;
  TODAYWEIGHT: IAnglerTodayWeightRedis;
  TOTALFISHCOUNT: string;
  TOTALWEIGHT: IAnglerTotalWeightRedis;
}

export interface IAnglerTMS {
  TieRank: number;
  TieQnty: number;
  TournamentID: number;
  TournamentDay: number;
  FishermenID: number;
  CompetitorClass: number;
  FirstName: string;
  LastName: string;
  MiddleInitial: string;
  FontName: string;
  City: string;
  State: string;
  hometown: string;
  teamid?: number;
  todays_FishCount: number;
  todays_FishDead: number;
  todays_TotalOunces: number;
  todays_TotalPdsOz: string;
  todays_TotalOuncesAfterPenalty: number;
  todays_TotalPdsOzAfterPenalty: string;
  todays_BigFishOuncesAfterPenalty: number;
  todays_ScaledFish: string;
  todays_bumpstamp: string;
  todays_fishcull: number;
  todays_FishReduced: number;
  todays_minuteslate: number;
  overall_SumTotalOuncesAfterPenalty: number;
  overall_SumTotalPdsOzAfterPenalty: string;
  accum_SumFishCount: number;
  partners_sum_totalouncesafterpenalty: number;
  Ranking_SumTotalOuncesAfterPenalty: number;
  Ranking_SumTotalPdsOzAfterPenalty: string;
  NeedsForLeadWt: number;
  NeedsForCutWt: number;
  TournamentPoints: number;
  RowNum: number;
  DailyWeight?: string;
  timestamp: number;
}

// Full active tournament
export interface ITournamentRedis {
  '@': {
    ID: string;
    DAY: string;
  };
  ANGLERS: {
    ANGLER: IAnglerRedis | IAnglerRedis[];
  };
}
// Tournament that has ended
export interface ITournamentEODRedis {
  REDIS_EOD_VALUE: boolean;
}
// Capture either a live or closed tournament
export type MaybeTournamentRedis = ITournamentEODRedis | ITournamentRedis;
// Helper guard function to determine between active and EOD tournament angler data
export function isEOD(
  tournament: MaybeTournamentRedis
): tournament is ITournamentEODRedis {
  return tournament[0]?.ENDOFDAY !== null;
}

/**
 * API angler historical tournament shows
 * @example - /v1/data/angler-stats/28709/expand
 */
export interface IAnglerStatsExpanded {
  rank: number;
  place: number;
  FishermenId: AnglerId;
  URL: string;
  Title: string;
  tourYear: number;
}

/**
 * API data showing BASSTrakk leaderboard anglers
 * @example - /v1/data/basstrakk-leaderboard/1225
 */
export interface IBasstrakkAngler {
  [s: string]: any;
  BTCull: number;
  BTCullOZ: number;
  BTCullPdsOz: string;
  BTFishCount: number;
  BTFishLivewell: number;
  BTKeptOz: number;
  BTKeptPdsOz: string;
  BTLimitOunces: number;
  BackOunces: number;
  BackPdsOz: string;
  FISHERMAN_PHONENAME: string;
  FISHERMENID: AnglerId;
  LastLatitude: string;
  LastLocationTime: string;
  LastLongitude: string;
  LimitPdsOz: string;
  OfficialFishCount: number;
  OfficialWeightOz: number;
  PartnerFontname: string;
  PartnerFishermenId: AnglerId;
  Rank: number;
  // sponsorLogos: ISponsors;
  SumFishCount: number;
  SumOunces: number;
  SumPdsOz: string;
  Timestamp: number;
  PollsLeft: number;
}

/**
 * API data showing BASSTrakk catches
 * @example - /v1/data/basstrakk-catches/1225
 */
export interface IBasstrakkCatch {
  AnglerId: AnglerId;
  FishWeightOz: number;
  FishWeightPdsOz: string;
  IsKept: 'Y' | 'N';
  Latitude: string;
  Longitude: string;
  Time: string;
  TournamentId: number;
  uid: string;
  PollsLeft: number;
}

/**
 * API data showing BASSTrakk catches with additional properties
 * @example - /v1/data/basstrakk-catches/1225
 *
 * This extends IBasstrakkCatch to add additional properties that are added
 * when useCatchesTableData() is called
 */
export interface ICatchesTableData extends IBasstrakkCatch {
  Angler: IBasstrakkAngler;
  Catches: IBasstrakkCatch[];
  BigBassLeader: IBasstrakkAngler;
}

export interface IAoyTableData extends ICatchesTableData, IBasstrakkAngler {
  AoyStanding: IAoyAnglerRank[];
}

export type CatchesTableColumns = TableCommonProps | ICatchesTableData;

export interface ICutline {
  cutlineValue: number;
  cutlineText: string;
}

export interface IHighlighted {
  sponsorLogos: string[];
  PollsLeft: number;
}

// Context
export const cutlineContext = React.createContext({
  cutlines: [] as ICutline[],
});

export type CutlineRow = (text: string, key: string) => JSX.Element;

export enum AnglerKeyToMatch {
  FISHERMENID = 'FISHERMENID',
  UID = 'uid',
}

export enum AnglerRowKeyToMatch {
  FISHERMENID = 'FISHERMENID',
}

export interface ICatchesSummary {
  keptTop5: IBasstrakkCatch[];
  keptTop5TotalWeight: number;
  keptTop5TimeSort: IBasstrakkCatch[];
  keptCount: number;
  culledCount: number;
}

// Sponsors
export interface ISponsors {
  [key: number]: ISponsorDetails;
}

export interface ISponsorDetails {
  imageUrl: string;
  anglers: AnglerId[];
}

export enum SponsorList {
  MERCURY_MOVES = 441,
}

// AOY
export interface IAoyAngler {
  id: AnglerId;
  type: AoyTournamentType;
  // Angler profile value referencing Drupal node.
  tms: string;
  name: string;
  location: string;
  score: number;
  tourYear: number;
  rank: number;
  city: string;
  weight: number;
}

// AOY Tournament
export interface IAoyTournament {
  place: number;
  Title: string;
  URL: string;
  final_weight: number;
  PointsEarned: number
}


export interface IAoyTournamentTMS {
  fishermenid: number;
  TournamentId: number;
  place: number;
  Points: number;
  Title: string;
  uid: string;
  timestamp: number;
  URL: string;
  City: string;
  State: string;
}

// New AOY Rank
export interface IAoyAnglerRank {
  AoyRank: number;
  PointsEarned: number;
  PointsTotal: number;
  Rank: number;
  FISHERMAN_PHONENAME: string;
  FISHERMENID: number;
  OfficialWeightOz: number;
  OfficialFishCount: number;
  BTLimitOunces: number;
  BTFishCount: number;
  LimitPdsOz: string;
  SumOunces: number;
  SumPdsOz: string;
  SumFishCount: number;
  BackOunces: number;
  BackPdsOz: string;
  BTFishLivewell: number;
  LastLatitude: string;
  LastLongitude: string;
  LastLocationTime: string;
}

export interface IAoyFirstPlaceAngler {
  name: string;
  score: number;
  city: string;
  location: string;
  tms: AnglerId;
}

export interface IAoyTableRow {
  Rank: JSX.Element;
  Name: JSX.Element;
  PTS: JSX.Element;
}

export enum AoyRanks {
  FIRST_PLACE = 1,
}

export enum AoyTournamentType {
  AOY = 91,
  ROY = 90,
  TOTAL_OPENS = 501,
  EASTERN_OPENS = 531,
  CENTRAL_OPENS = 551,
  NORTHERN_OPENS_PRO = 511,
  NORTHERN_OPENS_CO = 510,
  EASTERN_OPENS_PRO = 531,
  EASTERN_OPENS_CO = 530,
  CENTRAL_OPENS_PRO = 551,
  CENTRAL_OPENS_CO = 550,
  SOUTHERN_OPENS_PRO = 521,
  SOUTHERN_OPENS_CO = 520,
  DIVISION_1_CO = 1190,
  DIVISION_1_PRO = 1191,
  DIVISION_2_CO = 1200,
  DIVISION_2_PRO = 1201,
  DIVISION_3_CO = 1210,
  DIVISION_3_PRO = 1211,
}

export enum AoyTournamentName {
  AOY = 'Angler of the Year',
  ROY = 'Rookie of the Year',
  TOTAL_OPENS = 'Total Opens',
  NORTHERN_OPENS_PRO = 'Northern Opens - Pro',
  NORTHERN_OPENS_CO = 'Northern Opens - Co',
  EASTERN_OPENS_PRO = 'Eastern Opens - Pro',
  EASTERN_OPENS_CO = 'Eastern Opens - Co',
  CENTRAL_OPENS_PRO = 'Central Opens - Pro',
  CENTRAL_OPENS_CO = 'Central Opens - Co',
  SOUTHERN_OPENS_PRO = 'Southern Opens - Pro',
  SOUTHERN_OPENS_CO = 'Southern Opens - Co',
  DIVISION_1_CO = 'Opens Division 1 - Co',
  DIVISION_1_PRO = 'Opens Division 1 - Pro',
  DIVISION_2_CO = 'Opens Division 2 - Co',
  DIVISION_2_PRO = 'Opens Division 2 - Pro',
  DIVISION_3_CO = 'Opens Division 3 - Co',
  DIVISION_3_PRO = 'Opens Division 3 - Pro',
}

// Ads
export enum AdvertisementVariant {
  TOP = 'top_banner',
  BASSTRAKK = 'basstrakk_banner',
  BOX = 'basstrakk_box',
  CUTLINE = 'basstrakk_cutline',
  BIGBASS = 'bigbass_banner',
  CATCHES = 'catches_banner',
  AOY = 'aoy_banner',
  AOYCUTLINE = 'aoy_cutline',
}
export interface IAdvertisementProps {
  variant: AdvertisementVariant;
  count?: number | string;
}

export interface IAoySelectInterface {
  year?: number;
}

export interface IDailyWeightDataApi {
  DayNumber: number;
  FishCount: number;
  TotalOuncesAfterPenalty: number;
}

export interface ILeaderboardAnglerDataApi {
  TieRank: number;
  TieQnty: number;
  TournamentID: number;
  FishermenID: number;
  CompetitorClass: number;
  FirstName: string;
  LastName: string;
  MiddleInitial: string;
  FontName: string;
  City: string;
  State: string;
  hometown: string;
  teamid: number;
  todays_FishCount: number;
  todays_FishDead: number;
  todays_TotalOunces: number;
  todays_TotalPdsOz: string;
  todays_TotalOuncesAfterPenalty: number;
  todays_TotalPdsOzAfterPenalty: string;
  todays_BigFishOuncesAfterPenalty: number;
  todays_ScaledFish: number;
  todays_bumpstamp: number;
  todays_fishcull: number;
  todays_FishReduced: number;
  todays_minuteslate: number;
  overall_SumTotalOuncesAfterPenalty: number;
  overall_SumTotalPdsOzAfterPenalty: string;
  accum_SumFishCount: number;
  partners_sum_totalouncesafterpenalty: number;
  Ranking_SumTotalOuncesAfterPenalty: number;
  Ranking_SumTotalPdsOzAfterPenalty: string;
  NeedsForLeadWt: number;
  NeedsForCutWt: number;
  TournamentPoints: number;
  RowNum: number;
  DailyWeight: IDailyWeightDataApi[];
  timestamp: number;
}

export type PollsLeftMap = Map<string, number>;
